import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, TablePagination, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import type { IStreams } from "../../../types/streams";
import type { FrontendStatus } from "../../../types/bookings";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import { logout } from "../../../store/slice/AuthSlice";
import formatDate from "../../../utils/FormatDate";

import StatsSection from "./StatsSection";
import StreamListHeader2 from "./StreamListHeader2";
import StreamListTable2 from "./StreamListTable2";
import WeekCalendar from "./WeekCalendar";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

interface StreamResponse {
  id: number;
  booking_id: number;
  booking_status: string;
  venue_parent: { id: number; name: string } | null;
  venue_name: string | null;
  title: string;
  date: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  start: string;
  end: string;
  status: string;
  vimeo_download_password: string | null;
  vimeo_stream_link: string | null;
}

const statusMapToBackend: Record<FrontendStatus, string> = {
  enquiry: "pending",
  confirmed: "confirmed",
  fulfilled: "completed",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

const StreamListV2: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [streams, setStreams] = useState<IStreams[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [streamToDelete, setStreamToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const hasPermission = (permission: string) =>
    userPermissions.includes(permission.trim().replace(/'$/, ""));

  const fetchStreams = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      const response = await apiService.get<{
        count: number;
        results: StreamResponse[];
      }>("/api/streams/list-streams/", params);

      const mapped: IStreams[] = response.data.results.map((stream) => ({
        id: stream.id.toString(),
        bookingId: `B${stream.booking_id.toString().padStart(3, "0")}`,
        title: stream.title || "Untitled",
        clientName: "Unknown",
        serviceDate: formatDate(stream.date),
        dateOfService: stream.date,
        serviceAddress: `${
          stream.venue_parent
            ? `${stream.venue_parent.name} — ${stream.venue_name || ""}`
            : stream.venue_name || ""
        },${stream.address_one || ""}, ${stream.address_two || ""}, ${
          stream.suburb || ""
        }, ${stream.city || ""}`
          .replace(/, ,/g, ",")
          .replace(/,,/g, ",")
          .trim(),
        status: stream.status || "",
        originalStatus: "enquiry" as FrontendStatus,
        producer: "N/A",
        start: stream.start,
        end: stream.end,
        vimeo_stream_link: stream.vimeo_stream_link || null,
        vimeo_download_password: stream.vimeo_download_password || null,
        over_due_by: null,
        password_protected: !!stream.vimeo_download_password,
      }));

      setStreams(mapped);
      setRowCount(response.data.count);
    } catch (err: unknown) {
      const errorResponse = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      if (errorResponse?.response?.status === 401) {
        dispatch(logout());
        navigate("/login");
      } else {
        toast.error(
          errorResponse?.response?.data?.detail ||
            "Failed to load streams. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [appliedSearchTerm, dispatch, navigate, page, rowsPerPage]);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (inputValue !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(inputValue);
        setPage(0);
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inputValue, appliedSearchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      setAppliedSearchTerm(inputValue);
      setPage(0);
    }
  };

  const handleSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setAppliedSearchTerm(inputValue);
    setPage(0);
  };

  const handleRowClick = (id: string) => {
    navigate(`/stream-management/view/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/stream-management/view/${id}/edit`);
  };

  const handleStatusChange = async (id: string, newStatus: FrontendStatus) => {
    setStreams((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: newStatus } : s)),
    );
    try {
      await apiService.patch(`/api/streams/stream/${id}/`, {
        status: statusMapToBackend[newStatus],
      });

      if (newStatus === "fulfilled") {
        try {
          await apiService.post("/api/streams/stream/end-stream/", {
            stream_id: id,
          });
        } catch {
          toast.error("Stream status updated but checkout failed");
        }
      }

      toast.success("Stream status updated successfully");
    } catch {
      toast.error("Failed to update stream status");
      fetchStreams();
    }
  };

  const confirmDelete = (id: string) => {
    setStreamToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!streamToDelete) return;
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/streams/stream/${streamToDelete}/`);
      setStreams((prev) => prev.filter((s) => s.id !== streamToDelete));
      toast.success("Stream deleted successfully");
      setOpenDeleteDialog(false);
      setStreamToDelete(null);
    } catch {
      toast.error("Failed to delete stream");
      setOpenDeleteDialog(false);
      setStreamToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setStreamToDelete(null);
  };

  const handleCalendarStreamClick = (streamId: number) => {
    navigate(`/stream-management/view/${streamId}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ display: "flex", gap: 3, minHeight: "100vh" }}>
        <Box sx={{ flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <StatsSection />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: colors.primary[900],
                borderRadius: "12px",
                padding: "20px",
              }}
            >
              <StreamListHeader2
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSearch={handleSearch}
                onAddStream={() => navigate("/stream-management/add")}
                hasAddPermission={hasPermission("stream.add_stream")}
              />

              <StreamListTable2
                streams={streams}
                loading={loading}
                onRowClick={handleRowClick}
                onEdit={handleEdit}
                onDelete={confirmDelete}
                onStatusChange={handleStatusChange}
                hasEditPermission={hasPermission("stream.change_stream")}
                hasDeletePermission={hasPermission("stream.delete_stream")}
              />

              <TablePagination
                component="div"
                count={rowCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: "1px solid #E0E0E0",
                  "& .MuiTablePagination-toolbar": { justifyContent: "center" },
                }}
              />
            </Paper>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Box sx={{ width: "320px" }}>
            <WeekCalendar onStreamClick={handleCalendarStreamClick} />
          </Box>
        </motion.div>
      </Box>

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </motion.div>
  );
};

export default StreamListV2;
